import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  Container,
  FormFeedback,
  Form,
} from "reactstrap";
import { useFormik } from "formik";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import ResultHandler from "../../../components/ResultHandler";
import FormButtonHandler from "../../../components/FormButtonHandler";
import SearchSelectBox from "../../../components/SearchSelectBox";
import { useResponse } from "../../../hooks/useResponse";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { getAdmin, editAdmin } from "../../../api/admins";
import { getRoles } from "../../../api/roles";

const passwordReg = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/;
const strongPasswordMsg = "Password must be at least 8 characters long and include uppercase, number, and special character.";

const EditAdmin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [adminData, setAdminData] = useState(null);

  const { setData, loading, error, success } = useResponse(editAdmin, () => {
    navigate("/admins");
  });

  const validationSchema = Yup.object({
    name: Yup.string().required(t("validation.name")),
    email: Yup.string().email(t("validation.emailInvalid")).required(t("validation.email")),
    password: Yup.string()
      .notRequired()
      .test('password-strength', strongPasswordMsg, function (value) {
        if (!value) return true;
        return passwordReg.test(value);
      }),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], t("validation.confirmPass")),
    roleId: Yup.object()
      .shape({
        value: Yup.string().required(),
        label: Yup.string().required(),
      })
      .nullable()
      .required(t("validation.roleId")),
  });


  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        const res = await getAdmin(id);
        console.log("resresresres", res);
        const data = res.data;
        setAdminData({
          name: data.name || "",
          email: data.email || "",
          password: "",
          confirmPassword: "",
          roleId: data.roleId
            ? { value: data.roleId._id, label: data.roleId.name }
            : null,
        });
      } catch (err) {
        console.error("Failed to fetch admin data", err);
      }
    };

    fetchAdmin();
  }, [id]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: adminData || {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      roleId: null,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const updatedData = {
        name: values.name,
        email: values.email,
        password: values.password || undefined,
        roleId: values.roleId?.value,
      };
      localStorage.setItem("admin", JSON.stringify(updatedData.name));
      setData({ id, data: updatedData });
    },
  });

  return (
    <div className="page-content">
      <Container fluid={true}>
        <Breadcrumbs title={`${t("btns.Edit")} ${t("Admin")}`} />

        <Form
          className="needs-validation"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
          }}
        >
          <Row>
            <Col md="6">
              <FormGroup className="mb-3">
                <Label>{t("Name")}</Label>
                <Input
                  name="name"
                  placeholder={t("Name")}
                  type="text"
                  onChange={validation.handleChange}
                  value={validation.values.name}
                  invalid={!!(validation.touched.name && validation.errors.name)}
                />
                <FormFeedback>{validation.errors.name}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md="6">
              <FormGroup className="mb-3">
                <Label>{t("Email")}</Label>
                <Input
                  name="email"
                  placeholder={t("Email")}
                  type="email"
                  onChange={validation.handleChange}
                  value={validation.values.email}
                  invalid={!!(validation.touched.email && validation.errors.email)}
                />
                <FormFeedback>{validation.errors.email}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md="6">
              <FormGroup className="mb-3">
                <Label>{t("Password")}</Label>
                <Input
                  name="password"
                  placeholder={t("Password")}
                  type="password"
                  onChange={validation.handleChange}
                  value={validation.values.password}
                  invalid={!!(validation.touched.password && validation.errors.password)}
                />
                <FormFeedback>{validation.errors.password}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md="6">
              <FormGroup className="mb-3">
                <Label>{t("Confirm Password")}</Label>
                <Input
                  name="confirmPassword"
                  placeholder={t("Confirm Password")}
                  type="password"
                  onChange={validation.handleChange}
                  value={validation.values.confirmPassword}
                  invalid={!!(validation.touched.confirmPassword && validation.errors.confirmPassword)}
                />
                <FormFeedback>{validation.errors.confirmPassword}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md="6">
              <FormGroup className="mb-3">
                <Label>{t("Role")}</Label>
                <SearchSelectBox
                  api={getRoles}
                  name="roleId"
                  placeholder={t("Select Role")}
                  value={validation.values.roleId}
                  label="name"
                  setValue={(value) => validation.setFieldValue("roleId", value)}
                  error={!!(validation.touched.roleId && validation.errors.roleId)}
                />

                {validation.touched.roleId && validation.errors.roleId && (
                  <div className="text-danger mt-1">{validation.errors.roleId}</div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <FormButtonHandler btnTxt={t("Update")} loading={loading} />
          <ResultHandler
            error={error}
            success={success}
            successMsg={t("Updated Successfully!")}
          />
        </Form>
      </Container>
    </div>
  );
};

export default EditAdmin;
